/*
 * Figma `121:18` — OPS-01 · On-site Settings · Readiness.
 *
 * `EditorTemplate`: the settings on the left, the pre-publish checks in the rail, a save bar under
 * both. The frame's own shape, with one substitution and one subtraction.
 *
 * **The substitution.** The frame draws its checks as four labels with a coloured pill and nothing
 * behind them. The checks here come from `readiness.ts`, which is the definition of ready this
 * product already settled — so a failed check carries the count that failed it ("2 งานยังไม่มีคน
 * รับผิดชอบ") and a way to the screen that fixes it. A pill that says ต้องแก้ and stops there sends
 * someone looking; a pill that says what is missing sends them to fix it.
 *
 * **The subtraction.** The frame's main panel is four fields — door time, show start, show end,
 * venue capacity — and its KPI strip counts gates, equipment and unpublished changes. Of those
 * seven, one now exists: the event's end date, which reaches the console on this very record and is
 * shown read-only, because SuperAdmin owns it and the PATCH here has no field for it. The rest do
 * not: there is no door or start time, no capacity, no gate or equipment record, and no draft state
 * for settings that save immediately. Drawing them with invented numbers would make the one screen
 * whose job is to say whether the event is ready the screen that lies about it.
 */
import {
  Alert, Button, Card, EditorTemplate, KpiCard, SaveBar, StatusBadge, TextField, Toggle,
} from '../..';
import type { BadgeTone, SaveState } from '../..';
import type { Destination } from '../../destinations';
import type { CheckStatus, PhaseReadiness, ReadinessTab } from '../../../readiness';
import type { OpsEventSettingsState, PrepareReadinessState } from './useOpsEventSettings';
import { useOpsEventSettings, usePrepareReadiness } from './useOpsEventSettings';
import { countdownTo, countdownWords } from './eventDate';

const baht = (n: number) => `฿${n.toLocaleString('th-TH')}`;

const CHECK_LABEL: Record<CheckStatus, string> = {
  pass: 'ผ่าน', fail: 'ต้องแก้', not_applicable: 'ไม่เกี่ยวกับงานนี้', unreadable: 'ดูไม่ได้',
};
const CHECK_TONE: Record<CheckStatus, BadgeTone> = {
  pass: 'success', fail: 'danger', not_applicable: 'neutral', unreadable: 'warning',
};

const STATE_COPY: Record<PhaseReadiness['state'], { text: string; tone: 'positive' | 'warning' | 'danger' | 'neutral' }> = {
  ready: { text: 'พร้อมแล้ว', tone: 'positive' },
  not_ready: { text: 'ยังไม่พร้อม', tone: 'danger' },
  partial_view: { text: 'ดูได้ไม่ครบ', tone: 'warning' },
  no_access: { text: 'ไม่มีสิทธิ์ดู', tone: 'neutral' },
};

export function OpsEventSettingsScreen({
  destination, state, readiness, permitted, role, onGoTo,
}: {
  destination: Destination;
  state: OpsEventSettingsState;
  readiness: PrepareReadinessState;
  permitted: boolean;
  role?: string | null;
  /** Where a failed check is fixed. Omitted in the classic shell, where the rail is not drawn. */
  onGoTo?: (tab: ReadinessTab) => void;
}) {
  const {
    data, target, isOutdoor, parsed, dirty, saving, saved, error, loadError, conflict,
    setTarget, setIsOutdoor, discard, takeTheirs, save, reload,
  } = state;

  const saveState: SaveState = saving ? 'saving'
    : error ? 'error'
      : dirty ? 'dirty'
        : saved ? 'saved' : 'clean';

  const phase = readiness.readiness;
  const failing = phase ? phase.checks.filter((c) => c.status === 'fail') : [];
  const blockingFailures = failing.filter((c) => c.blocking).length;

  return (
    <EditorTemplate
      gate={{ destination, permitted, role, loading: !data && !loadError, error: loadError || null, onRetry: reload }}
      kpis={
        <>
          <KpiCard
            label="ความพร้อมเฟสเตรียมงาน"
            value={readiness.loading ? '—' : phase?.percent === null || !phase ? 'ยังบอกไม่ได้' : `${phase.percent}%`}
            tone={phase ? STATE_COPY[phase.state].tone : 'neutral'}
            meta={
              readiness.error ? readiness.error
                : readiness.loading ? 'กำลังตรวจ…'
                  : phase ? `${phase.passed}/${phase.applicable} checks ผ่าน · ${STATE_COPY[phase.state].text}`
                    : ''
            }
          />
          <KpiCard
            label="เป้ารายได้"
            value={data && data.targetRevenue !== null ? baht(data.targetRevenue) : 'ยังไม่ตั้ง'}
            tone={data && data.targetRevenue !== null ? 'neutral' : 'warning'}
            meta={
              data && data.targetRevenue !== null
                ? 'ใช้วัดความคืบหน้ายอดขายของงานนี้'
                : 'เป็นหนึ่งใน check ที่ต้องผ่านก่อนถือว่าเตรียมงานพร้อม'
            }
          />
          <KpiCard
            label="วันจบงาน"
            value={countdownWords(countdownTo(data?.eventEndsAt ?? null, data?.eventTimezone ?? null))}
            meta={
              data?.eventEndsAt
                ? `${new Date(data.eventEndsAt).toLocaleString('th-TH')}${data.eventTimezone ? ` · ${data.eventTimezone}` : ''}`
                : 'ยังไม่ได้ตั้ง — ทีมงาน JamTick เป็นคนตั้งให้ ไม่ได้แก้จากหน้านี้'
            }
          />
          <KpiCard
            label="แก้ไขล่าสุด"
            value={data?.updatedAt ? new Date(data.updatedAt).toLocaleDateString('th-TH') : 'ยังไม่เคยแก้'}
            meta={
              data?.updatedAt
                ? `${new Date(data.updatedAt).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}${data.updatedBy ? ` · ${data.updatedBy}` : ''}`
                : 'ค่าที่เห็นคือค่าตั้งต้นของระบบ'
            }
          />
        </>
      }
      sections={
        <Card
          title="การตั้งค่าหลัก"
          sub="เป้าหมายและลักษณะงานที่ทีมงานใช้วางแผน — ไม่เกี่ยวกับราคาบัตรหรือสิ่งที่ผู้ซื้อเห็น"
        >
          {conflict && (
            <Alert tone="warning" title="มีคนอื่นบันทึกไปก่อนแล้ว">
              <p>
                ค่าล่าสุดในระบบคือ เป้ารายได้{' '}
                <b>{conflict.targetRevenue === null ? 'ยังไม่ตั้ง' : baht(conflict.targetRevenue)}</b>{' '}
                และงานกลางแจ้ง <b>{conflict.isOutdoor ? 'ใช่' : 'ไม่ใช่'}</b>
                {conflict.updatedBy ? ` (โดย ${conflict.updatedBy})` : ''}
              </p>
              <div className="jt-drawer-guard">
                <Button size="sm" variant="secondary" disabled={saving} onClick={takeTheirs}>
                  ใช้ค่าล่าสุดแทน
                </Button>
                <Button size="sm" variant="primary" disabled={saving} onClick={() => void save()}>
                  บันทึกทับด้วยค่าที่ฉันกรอก
                </Button>
              </div>
            </Alert>
          )}

          <TextField
            label="เป้ารายได้ (บาท)"
            inputMode="numeric"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="เว้นว่าง = ยังไม่ตั้งเป้า"
            error={parsed.invalid ? 'เป้ารายได้ต้องเป็นจำนวนเต็มบาท ไม่ติดลบ' : null}
            hint="จำนวนเต็ม หน่วยบาท · เว้นว่างแปลว่ายังไม่ได้ตั้งเป้า ซึ่งไม่เหมือนกับตั้งเป้าไว้ที่ 0"
          />

          <Toggle
            label="งานกลางแจ้ง"
            checked={isOutdoor}
            onChange={(e) => setIsOutdoor(e.target.checked)}
            consequence="ใช้ประกอบการวางแผนความเสี่ยงเรื่องสภาพอากาศ — ไม่กระทบการขายหรือสิ่งที่ผู้ซื้อเห็น"
          />
        </Card>
      }
      preview={
        <Card title="Pre-publish checks" sub="เช็กของเฟสเตรียมงาน ตามนิยามความพร้อมของระบบ">
          {readiness.error ? (
            <Alert tone="danger" title="ตรวจความพร้อมไม่สำเร็จ">{readiness.error}</Alert>
          ) : readiness.loading ? (
            <p className="jt-field-hint">กำลังตรวจ…</p>
          ) : !phase ? null : (
            <>
              {blockingFailures > 0 && (
                <Alert tone="warning" title={`มี ${blockingFailures} ข้อที่ต้องผ่านก่อนถึงจะถือว่าพร้อม`}>
                  เปอร์เซ็นต์ไม่ใช่คำตอบ — เฟสนี้จะพร้อมก็ต่อเมื่อข้อที่บังคับผ่านครบ
                </Alert>
              )}
              <ul className="jt-checks">
                {phase.checks.map((check) => (
                  <li key={check.id} className="jt-check">
                    <div className="jt-check-copy">
                      <p className="jt-check-label">
                        {check.label}
                        {check.blocking && <span className="jt-check-blocking"> · บังคับ</span>}
                      </p>
                      {check.detail && <p className="jt-check-detail">{check.detail}</p>}
                      {check.status === 'unreadable' && (
                        <p className="jt-check-detail">สิทธิ์ของบัญชีนี้อ่านข้อมูลที่ใช้ตรวจข้อนี้ไม่ได้</p>
                      )}
                      {/*
                        * Not for a check whose home is this screen — `target_revenue` is set in the
                        * form on the left of this very page, so its button navigated from here to
                        * here and did nothing at all. A control that cannot move you is worse than
                        * no control: it reads as the screen being broken.
                        */}
                      {check.status === 'fail' && onGoTo && check.tab !== destination.key && (
                        <Button size="sm" variant="ghost" onClick={() => onGoTo(check.tab)}>ไปแก้ที่หน้านั้น</Button>
                      )}
                    </div>
                    <span className="jt-check-trail">
                      <StatusBadge tone={CHECK_TONE[check.status]}>{CHECK_LABEL[check.status]}</StatusBadge>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      }
      stickyActions={
        <SaveBar
          state={saveState}
          onSave={() => void save()}
          onDiscard={discard}
          error={error || null}
          blockedReason={parsed.invalid ? 'เป้ารายได้ยังไม่ถูกต้อง' : null}
          consequence="ค่าเหล่านี้ใช้ในการวางแผนของทีมงาน ไม่กระทบออร์เดอร์หรือหน้าขาย"
          saveLabel="บันทึกการตั้งค่า"
        />
      }
    />
  );
}

export function OpsEventSettingsDestination({ token, eventSlug, destination, permitted, role, onGoTo }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
  onGoTo?: (tab: ReadinessTab) => void;
}) {
  const state = useOpsEventSettings(token, eventSlug);
  const readiness = usePrepareReadiness(token, eventSlug, state.data);
  return (
    <OpsEventSettingsScreen
      destination={destination}
      state={state}
      readiness={readiness}
      permitted={permitted}
      role={role}
      onGoTo={onGoTo}
    />
  );
}
